import React from 'react'
import T from 'prop-types'
import _ from 'lodash'
import cx from 'classnames'
import TextInput from './TextInput'
import TypeaheadMenuOwner from './TypeaheadMenuOwner'
import { keyCodes } from '../reactBaseConstants'

const DEFAULT_OPTION_HEIGHT = 29

export default class TypeaheadInput extends React.Component {
  static displayName = 'TypeaheadInput'

  static propTypes = {
    // Passthrough for TypeaheadMenuOwner
    maxOptsToShow: T.number, // Scroll the dropdown menu when more than this amount of options are present
    menuClassName: T.string,
    menuFullWidth: T.bool,
    optionHeight: T.number,
    stopClickPropagation: T.bool,
    noMatchesText: T.string,

    className: T.string,
    clearOnSave: T.bool,
    disabled: T.bool,
    inputClassName: T.string,
    'data-tooltip': T.string,
    omitCaret: T.bool,
    onChange: T.func.isRequired,
    onChangeArgs: T.array,
    onKeyUp: T.func,
    loading: T.bool,
    onMenuClose: T.func,
    onMenuOpen: T.func,
    options: T.array.isRequired, // List of options available for fuzzaldrin to search
    placeholder: T.string,
    focusOnMount: T.bool,
    readOnly: T.bool,
    resetMenuOnSave: T.bool, // If true (and if clearOnSave is true), input will be re-focused and menu reset when an option is saved
    sanitizer: T.func,
    saveOnBlur: T.bool,
    validateOnChange: T.bool,
    validator: T.func,
    preventAutoSelect: T.bool,
    swallowTab: T.bool, // If true (default) tab hits will be captured/swallowed
    valueProp: T.string,
    initialValue: T.oneOfType([T.string, T.number]),
    saveEmptyStrings: T.bool,
    activeOptionId: T.any,
    groupByHeadingText: T.bool
  }

  static defaultProps = {
    clearOnSave: true,
    disabled: false,
    maxOptsToShow: 10,
    menuFullWidth: false,
    noMatchesText: 'No matches',
    omitCaret: false,
    options: [],
    optionHeight: DEFAULT_OPTION_HEIGHT,
    className: '',
    placeholder: '',
    focusOnMount: false,
    readOnly: false,
    resetMenuOnSave: true,
    saveOnBlur: false,
    preventAutoSelect: false,
    swallowTab: true,
    valueProp: 'name',
    inputClassName: 'grid_input',
    groupByHeadingText: false
  }

  constructor(props) {
    super(props)
    this._refs = {}
    this.state = {
      typeaheadValue: '',
      typeaheadOptions: this.props.options,
      // fuzzyMatches: [],
      // selectionIdx: -1
      isMenuOpen: false,
      filterString: ''
    }
  }

  componentDidMount() {
    const { focusOnMount } = this.props
    if (focusOnMount) {
      this.focus()
    }
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    if (!_.isEqual(nextProps.options, this.props.options)) {
      this.setState({
        typeaheadOptions: nextProps.options
      })
    }

  }

  openMenu = () => {
    const { onMenuOpen } = this.props
    // const _initialValue = this._getInitialValue()
    // if (_initialValue) {
    //   // Force keyup flow with initial value
    //   this.handleKeyUp({
    //     target:{value: _initialValue}
    //   })
    // }
    this.setState({
      isMenuOpen: true
    })
    if (onMenuOpen) {
      onMenuOpen()
    }
  }

  closeMenu = () => {
    const { onMenuClose } = this.props
    this.setState({
      filterString: '',
      isMenuOpen: false
    })
    if (onMenuClose) {
      onMenuClose()
    }
  }

  getInputRef = () => {
    return this._refs.input
  }

  getMenuRef = () => {
    return this._refs.typeaheadMenu
  }

  _onApplyMenuOption = option => {
    const { validator, onChange, onChangeArgs=[], resetMenuOnSave, clearOnSave } = this.props
    const invalidMessage = validator ? validator(option.name) : null

    if (invalidMessage != null) {
      this.setState({
        invalidMessage
      })
    } else {
      onChange(option, ...onChangeArgs)
      if (clearOnSave) {
        this._refs.input.setValue('')
        if (resetMenuOnSave) {
          this._refs.input.focus()
          this._refs.typeaheadMenu.resetMenu()
        } else {
          this.closeMenu()
        }
      } else {
        this._refs.input.setValue(option.name)
        this.closeMenu()
      }
    }
  }

  focus = () => {
    if (this._refs && this._refs.input && !this.props.readOnly) {
      this._refs.input.focus()
    }
  }

  _receiveKey = e => {
    if (!this._refs.typeaheadMenu) {
      return false
    }

    switch (e.keyCode) {
      case keyCodes.enter:
        this._refs.typeaheadMenu.selectOption(e.target.value)
        return true
      case keyCodes.tab:
        this._refs.typeaheadMenu.selectOption(e.target.value)
        return true
      case keyCodes.up:
        e.preventDefault()
        this._refs.typeaheadMenu.moveSelection(true)
        return true
      case keyCodes.down:
        e.preventDefault()
        this._refs.typeaheadMenu.moveSelection(false)
        return true
      // case keyCodes.left:
      //   return true
      // case keyCodes.right:
      //   return true
      default:
    }
    return false
  }

  handleKeyDown = e => {
    const {
      swallowTab
    } = this.props

    this._keyHandled = this._receiveKey(e)

    if (this._keyHandled) {
      if (!swallowTab && e.keyCode === keyCodes.tab) {
        // Allow event propagation
        return
      }
      e.stopPropagation()
      e.preventDefault()
    }
  }

  handleKeyUp = e => {
    if (this._keyHandled) {
      return
    }
    const value = e.target.value
    // this.props.filterOptions(value)
    // this.props.filterOptions(value)
    this.setState({
      filterString: value
    })
    if (this.props.onKeyUp) {
      this.props.onKeyUp(value)
    }
  }

  handleCaretClick = () => {
    if (!this.props.disabled) {
      this.openMenu()
      this.focus()
    }
  }

  _saveUnmatchedValue = val => {
    this._refs.typeaheadMenu.saveUnmatchedValue(val)
  }

  _bindInputRef = textInput => {
    this._refs.input = textInput
  }

  _bindMenuRef = menu => {
    this._refs.typeaheadMenu = menu
  }

  _getInitialValue = () => {
    const {
      options,
      initialValue,
      valueProp
    } = this.props
    return _.get(_.find(options, opt => opt[valueProp] === initialValue), 'name', initialValue)
  }

  render() {
    const {
      optionHeight,
      loading,
      readOnly,
      className,
      inputClassName,
      omitCaret,
      clearOnSave,
      disabled,
      saveOnBlur,
      sanitizer,
      placeholder,
      stopClickPropagation,
      validateOnChange,
      validator,
      maxOptsToShow,
      menuClassName,
      menuFullWidth,
      noMatchesText,
      preventAutoSelect,
      saveEmptyStrings,
      activeOptionId,
      groupByHeadingText
    } = this.props
    const {
      isMenuOpen,
      filterString,
      typeaheadOptions,
      invalidMessage
    } = this.state

    const classes = cx('typeahead_input', {
      loading: loading,
      readonly: readOnly,
      menu_open: isMenuOpen
    })

    const inputClasses = cx('typeahead_input_el', inputClassName, {
      pw_text_input_invalid: !!invalidMessage
    })

    const _initialValue = this._getInitialValue()

    // Allow for options that should display if present, but not be offered as typeahead items
    const _options = _.reject(typeaheadOptions, opt => opt.readOnlyOption === true)

    return (
      <div
        className={classes + ' ' + className}
        onClick={this.handleCaretClick}
      >
        <TypeaheadMenuOwner
          closeMenu={this.closeMenu}
          isMenuOpen={isMenuOpen}
          optionHeight={optionHeight}
          onApplyMenuOption={this._onApplyMenuOption}
          filterString={filterString}
          options={_options}
          ref={this._bindMenuRef}
          stopClickPropagation={stopClickPropagation}
          maxOptsToShow={maxOptsToShow}
          menuClassName={menuClassName}
          menuFullWidth={menuFullWidth}
          noMatchesText={noMatchesText}
          preventAutoSelect={preventAutoSelect}
          saveEmptyStrings={saveEmptyStrings}
          activeOptionId={activeOptionId}
          groupByHeadingText={groupByHeadingText}
        >
          <TextInput
            className={inputClasses}
            clearOnSave={clearOnSave}
            disabled={disabled}
            onFocus={this.openMenu}
            onBlur={this.closeMenu}
            initialValue={_initialValue}
            onKeyDown={this.handleKeyDown}
            onKeyUp={this.handleKeyUp}
            onSave={this._saveUnmatchedValue}
            placeholder={placeholder}
            ref={this._bindInputRef}
            sanitizer={sanitizer}
            saveOnBlur={saveOnBlur}
            saveOnEnter={false}
            tooltip={invalidMessage || this.props['data-tooltip']}
            validateOnChange={validateOnChange}
            validator={validator}
          />
          {omitCaret
            ? null
            : <span className="caret" onClick={this.handleCaretClick} />}
        </TypeaheadMenuOwner>
      </div>
    )
  }
}
